/**
 * Voices List Page
 *
 * Main voices management page with filtering, pagination, and CRUD actions.
 *
 * Acceptance Criteria: AC 29, 34
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useVoices,
  useUpdateVoice,
  useDeleteVoice,
  useProviders,
  type VoiceFilters,
  type VoiceGender,
  type VoiceResponse,
} from '../services/voiceApi';
import { EditVoiceForm } from '../components/EditVoiceForm';
import { TestVoiceModal } from '../components/TestVoiceModal';
import { VoiceNavigation } from '../components/VoiceNavigation';
import { useInitialData } from '../../../contexts/InitialDataContext';

export function VoicesListView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<VoiceFilters>({
    page: 1,
    limit: 50,
  });

  const [selectedVoice, setSelectedVoice] = useState<VoiceResponse | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState<VoiceResponse | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<number | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);

  // Load data from initial data context and APIs
  const { state: initialDataState } = useInitialData();
  const { data: voices, isLoading, error } = useVoices(filters);
  const { data: providers, isLoading: isLoadingProviders } = useProviders();
  const updateVoiceMutation = useUpdateVoice();
  const deleteVoiceMutation = useDeleteVoice();

  const languages = initialDataState.data?.languages || [];

  // Initialize filter from URL query parameters
  useEffect(() => {
    const providerIdParam = searchParams.get('provider');
    if (providerIdParam) {
      setFilters((prev) => ({
        ...prev,
        providerId: Number(providerIdParam),
      }));
    }
  }, [searchParams]);

  // Debounce search with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update filters when debounced search changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      // Note: search is not in the VoiceFilters type, this is a simplified example
      page: 1,
    }));
    setPage(1);
  }, [debouncedSearch]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  const handleFilterChange = (
    key: keyof VoiceFilters,
    value: string | number | boolean | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      page: 1,
    }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleEdit = (voice: VoiceResponse) => {
    setSelectedVoice(voice);
    setShowEditForm(true);
  };

  const handleTest = (voice: VoiceResponse) => {
    setSelectedVoice(voice);
    setShowTestModal(true);
  };

  const handleToggleActive = async (voice: VoiceResponse) => {
    try {
      await updateVoiceMutation.mutateAsync({
        voiceId: voice.id,
        update: { isActive: !voice.isActive },
      });
      toast.success(`Voice ${voice.isActive ? 'deactivated' : 'activated'} successfully!`);
    } catch {
      toast.error(`Failed to ${voice.isActive ? 'deactivate' : 'activate'} voice`);
    }
  };

  const handleDelete = (voice: VoiceResponse) => {
    setVoiceToDelete(voice);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!voiceToDelete) return;

    try {
      await deleteVoiceMutation.mutateAsync(voiceToDelete.id);
      toast.success('Voice deleted successfully!');
      setShowDeleteConfirm(false);
      setVoiceToDelete(null);
    } catch {
      toast.error('Failed to delete voice');
    }
  };

  const filteredVoices =
    voices?.filter((voice) => voice.name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
    [];

  if (isLoading || isLoadingProviders || initialDataState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading voices</p>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  if (initialDataState.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading initial data</p>
          <p className="text-gray-600">{initialDataState.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <VoiceNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Voice Management</h1>
        <div className="flex space-x-3"></div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name..."
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="search-input"
          />

          {/* Provider Filter */}
          <select
            value={filters.providerId || ''}
            onChange={(e) => handleFilterChange('providerId', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="provider-filter"
          >
            <option value="">All Providers</option>
            {providers?.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>

          {/* Language Filter */}
          <select
            value={filters.languageCode || ''}
            onChange={(e) => handleFilterChange('languageCode', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="language-filter"
          >
            <option value="">All Languages</option>
            {languages.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name}
              </option>
            ))}
          </select>

          {/* Gender Filter */}
          <select
            value={filters.gender || ''}
            onChange={(e) => handleFilterChange('gender', e.target.value as VoiceGender)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="gender-filter"
          >
            <option value="">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="unclear">Unclear</option>
          </select>

          {/* Status Filter */}
          <select
            value={
              filters.isActive === undefined ? 'all' : filters.isActive ? 'active' : 'inactive'
            }
            onChange={(e) =>
              handleFilterChange(
                'isActive',
                e.target.value === 'all' ? undefined : e.target.value === 'active'
              )
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Empty State */}
      {filteredVoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg mb-4">
            No voices found. Browse and add voices from TTS providers to get started.
          </p>
          <button
            onClick={() => navigate('/voice-providers')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            data-testid="go-to-providers-button"
          >
            Go to Providers
          </button>
        </div>
      ) : (
        <>
          {/* Voices Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200" data-testid="voices-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Languages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gender
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sample
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVoices.map((voice) => (
                  <tr key={voice.id} data-testid={`voice-row-${voice.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{voice.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{voice.providerName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {voice.languageCodes.map((lang: string) => (
                          <span
                            key={lang}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {voice.localeName ? (
                        <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                          {voice.localeName}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Not set</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">{voice.gender}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {voice.sampleAudioUrl ? (
                        <button
                          onClick={() => {
                            const isPlaying = playingVoiceId === voice.id;

                            if (isPlaying && currentAudio) {
                              // Stop current audio
                              currentAudio.pause();
                              currentAudio.currentTime = 0;
                              setPlayingVoiceId(null);
                              setCurrentAudio(null);
                            } else {
                              // Stop any currently playing audio
                              if (currentAudio) {
                                currentAudio.pause();
                                currentAudio.currentTime = 0;
                              }

                              // Play new audio
                              const audio = new Audio(voice.sampleAudioUrl!);
                              audio.play();
                              setPlayingVoiceId(voice.id);
                              setCurrentAudio(audio);

                              // Reset state when audio ends
                              audio.addEventListener('ended', () => {
                                setPlayingVoiceId(null);
                                setCurrentAudio(null);
                              });
                            }
                          }}
                          className="text-gray-700 hover:text-gray-900 text-lg"
                          data-testid="play-sample-button"
                          title={
                            playingVoiceId === voice.id ? 'Stop sample audio' : 'Play sample audio'
                          }
                        >
                          {playingVoiceId === voice.id ? '⏸' : '▶'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">No sample</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          voice.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {voice.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(voice)}
                          className="text-blue-600 hover:text-blue-800"
                          data-testid="edit-button"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleTest(voice)}
                          className="text-green-600 hover:text-green-800"
                          data-testid="test-button"
                        >
                          Test
                        </button>
                        <button
                          onClick={() => handleToggleActive(voice)}
                          className="text-yellow-600 hover:text-yellow-800"
                          data-testid="toggle-active-button"
                        >
                          {voice.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(voice)}
                          className="text-red-600 hover:text-red-800"
                          data-testid="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Page {page} - Showing {filteredVoices.length} voices
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                data-testid="prev-page-button"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={filteredVoices.length < 50}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                data-testid="next-page-button"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {selectedVoice && (
        <>
          <EditVoiceForm
            isOpen={showEditForm}
            onClose={() => {
              setShowEditForm(false);
              setSelectedVoice(null);
            }}
            voice={selectedVoice}
          />

          <TestVoiceModal
            isOpen={showTestModal}
            onClose={() => {
              setShowTestModal(false);
              setSelectedVoice(null);
            }}
            provider={{
              id: selectedVoice.providerId,
              name: selectedVoice.providerName,
              hasApiKey: true,
              createdAt: '',
              updatedAt: '',
            }}
            providerVoiceId={selectedVoice.providerVoiceId}
            voiceName={selectedVoice.name}
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && voiceToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          data-testid="delete-confirmation-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{voiceToDelete.name}"? This will soft-delete the
              voice.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setVoiceToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="cancel-delete-button"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteVoiceMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
                data-testid="confirm-delete-button"
              >
                {deleteVoiceMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
